import { getProperty, getPropertyAnytimeRate, getPropertyRates } from 'api/accommodation'
import { OptimizelyExperiments, OptimizelyFeatureFlags } from 'constants/optimizely'
import buildPropertyRatesKey from 'lib/offer/buildPropertyRatesKey'
import { getOptimizelyExperimentVariation, getOptimizelyEnabledDecision } from 'lib/optimizely/optimizelyUtils'
import { getSource } from 'selectors/offerSelectors'

import { API_CALL, PROPERTY_UPDATE_PRICING_DATA } from './actionConstants'
import {
  FETCH_PROPERTY,
  FETCH_PROPERTY_ANYTIME_RATE,
  FETCH_PROPERTY_RATES,
} from './apiActionConstants'
import type { AppAction } from './types/ActionTypes'

interface FetchPropertyOptions {
  /**
   * Explicit override for the content variant flag. When provided, skips the
   * Optimizely state lookup. Used by the SSR layer to force base content for
   * the cached trip (`false`) while letting client-side SPA callers derive
   * the value from experiment state (omit or `undefined`).
   */
  useContentVariant?: boolean
}

export function fetchProperty(propertyId: string, options?: FetchPropertyOptions): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    if (!propertyId || state.offer.properties[propertyId]) return

    const useContentVariant = options?.useContentVariant !== undefined
      ? options.useContentVariant
      : getOptimizelyEnabledDecision(
          getOptimizelyExperimentVariation(state, OptimizelyExperiments.croLppCustomContentV2),
        )

    dispatch({
      type: API_CALL,
      api: FETCH_PROPERTY,
      request: () => getProperty(propertyId, {
        regionCode: state.geo.currentRegionCode,
        currency: state.geo.currentCurrency,
        previewMode: state.system.previewMode,
        useContentVariant: useContentVariant ? true : undefined,
      }),
      propertyId,
    })
  }
}

/**
 * Re-fetches property content with `copyMode: 'custom-default'`, bypassing
 * the "already in store" guard so it overwrites the base content loaded by
 * {@link fetchProperty}. Only used by the SSR layer as trip 2 for users
 * bucketed into the custom-content experiment variant.
 */
export function refetchPropertyContentVariant(propertyId: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    if (!propertyId) return

    dispatch({
      type: API_CALL,
      api: FETCH_PROPERTY,
      request: () => getProperty(propertyId, {
        regionCode: state.geo.currentRegionCode,
        currency: state.geo.currentCurrency,
        previewMode: state.system.previewMode,
        useContentVariant: true,
      }),
      propertyId,
    })
  }
}

export function fetchPropertyAnytimeRate(propertyId: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    if (!propertyId || state.offer.propertyAnytimeRates[propertyId]) return

    dispatch({
      type: API_CALL,
      api: FETCH_PROPERTY_ANYTIME_RATE,
      request: () => getPropertyAnytimeRate(propertyId, {
        regionCode: state.geo.currentRegionCode,
        currency: state.geo.currentCurrency,
      }),
      propertyId,
    })
  }
}

interface PropertyRatesParams {
  checkIn: string
  checkOut: string
  occupancies: Array<App.Occupants>
  utmContent: string | undefined
  roomTypeId: string | undefined
  roomRateId: string | undefined
  refresh?: boolean
}

export function fetchPropertyRates(propertyId: string, {
  checkIn,
  checkOut,
  occupancies,
  utmContent,
  roomTypeId,
  roomRateId,
  refresh = false,
}: PropertyRatesParams): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const source = getSource(state)

    const key = buildPropertyRatesKey({
      propertyId,
      checkIn,
      checkOut,
      occupancies,
      utmContent,
      roomTypeId,
      roomRateId,
    })
    // NB: For better or worse, we have no TTL on cached responses, so rates _will_ become stale and
    // errors _won't_ clear until a hard refresh.
    if (!key || (!refresh && state.offer.propertyRates[key])) return

    dispatch({
      type: API_CALL,
      api: FETCH_PROPERTY_RATES,
      request: () => getPropertyRates(propertyId, {
        checkIn,
        checkOut,
        occupancies,
        regionCode: state.geo.currentRegionCode,
        currency: state.geo.currentCurrency,
        previewMode: state.system.previewMode,
        source,
        dynamicAvailability: true,
        upliftRatesWithInclusions: state.system.features.flags[OptimizelyFeatureFlags.sppUpliftLpcRatesWithInclusions] ? 'lpc' : false,
        enablePropertyCollect: state.system.features.flags[OptimizelyFeatureFlags.croPayAtProperty],
        includeBundles: getOptimizelyEnabledDecision(getOptimizelyExperimentVariation(state, OptimizelyExperiments.sppIncludeBundles)),
        utmContent,
        roomTypeId,
        roomRateId,
        areFlightsEnabled: state.offer.properties[propertyId]?.property?.areFlightsEnabled ?? false,
      }),
      key,
    })
  }
}

interface PropertyPricingDataParams {
  checkIn: string
  checkOut: string
  occupancies: Array<App.Occupants>
  utmContent: string | undefined
  roomTypeId: string | undefined
  roomRateId: string | undefined
  roomId: string
  rateId: string
  newPrice: number
  newLuxPlusPrice: number | undefined
  newPropertyFees: number
}

export function updatePropertyPricingData(propertyId: string, {
  checkIn,
  checkOut,
  occupancies,
  utmContent,
  roomTypeId,
  roomRateId,
  roomId,
  rateId,
  newPrice,
  newLuxPlusPrice,
  newPropertyFees,
}: PropertyPricingDataParams): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const key = buildPropertyRatesKey({
      propertyId,
      checkIn,
      checkOut,
      occupancies,
      utmContent,
      roomTypeId,
      roomRateId,
    })
    if (!key || !state.offer.propertyRates[key]) return

    dispatch({
      type: PROPERTY_UPDATE_PRICING_DATA,
      key,
      roomId,
      rateId,
      newPrice,
      newLuxPlusPrice,
      newPropertyFees,
    })
  }
}
